import React, { useEffect, useRef, useState } from "react";
import {
  useWebSocketContext,
  AlertParser,
  useNotification,
  NotificationType,
} from "@moonscape/shared";
import toast from "react-hot-toast";

export const GlobalAlert = () => {
  const { messages, setMessages } = useWebSocketContext();
  const { notifications, remove: removeNotification } = useNotification();
  const [queue, setQueue] = useState<string[]>([]);
  const queueRef = useRef<string[]>([]);
  const isShowingRef = useRef(false);

  useEffect(() => {
    queueRef.current = queue;
  }, [queue]);

  // Add new messages to queue
  useEffect(() => {
    if (messages.length > 0) {
      const message = messages[0];

      // Skip sync messages - they're handled by SyncProgressBanner
      if (message.type === "sync" || message.type === "sync_progress" || message.type === "sync_state_change") {
        setMessages((prev) => prev.slice(1));
        return;
      }

      const alertParser = new AlertParser(message);
      if (alertParser?.alertText) {
        setQueue((prev) => [...prev, alertParser]);
      }
      setMessages((prev) => prev.slice(1));
    }
  }, [messages]);

  // Add new messages to queue
  useEffect(() => {
    if (notifications.length > 0) {
      const alert = notifications[0];
      if (alert?.alertText) {
        setQueue((prev) => [...prev, alert]);
      }
      removeNotification(alert.id);
    }
  }, [notifications]);

  // Toast processing loop
  useEffect(() => {
    const interval = setInterval(() => {
      if (isShowingRef.current || queueRef.current.length === 0) return;

      const nextMessage: any = queueRef.current[0];
      isShowingRef.current = true;
      if (!nextMessage) return;

      const toastId =
        nextMessage?.type === NotificationType.ERROR
          ? toast.error(nextMessage?.alertText, {
              duration: Infinity,
            })
          : toast.success(nextMessage?.alertText, {
              duration: Infinity,
            });

      const dismissToast = () => {
        toast.dismiss(toastId);
        isShowingRef.current = false;
      };

      const handleVisibilityChange = () => {
        if (!document.hidden) {
          setTimeout(dismissToast, 3000);
          document.removeEventListener(
            "visibilitychange",
            handleVisibilityChange
          );
        }
      };

      if (document.hidden) {
        document.addEventListener("visibilitychange", handleVisibilityChange);
      } else {
        setTimeout(dismissToast, 3000);
      }

      // Remove from queue
      queueRef.current = queueRef.current.slice(1);
      setQueue(queueRef.current);
    }, 500); // check every 0.5s

    return () => clearInterval(interval);
  }, []);

  return null;
};
